import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import moment from "moment";
import {
  fetchAppointmentsService,
  fetchAppointmentDetailsService,
  sendDB4Service,
  sendAppointmentNotificationService,
  sendPatientNotificationService,
} from "../../services/db4";
import { setAppError } from "../appSlice";
import { transformAppointment } from "../../utils";
import { serviceBenefitsMapping } from "../../constants";

const db4Slice = createSlice({
  name: "db4",
  initialState: {
    needSearch: true,
    db4FromDate: moment().format("YYYY-MM-DD"),
    db4ToDate: moment().format("YYYY-MM-DD"),
    selectedDb4AppointmentId: "",
    appointments: [],
    cachedAppointmentDetails: {},
    db4Notification: {},
    db4AppointmentToDoctorNotification: {},
    db4AppointmentToPatientNotification: {},
  },
  reducers: {
    resetSearch(state) {
      state.needSearch = false;
    },
    setDb4FromDate(state, action) {
      const { db4FromDate } = action.payload;
      state.db4FromDate = db4FromDate;
      state.needSearch = true;
    },
    setDb4ToDate(state, action) {
      const { db4ToDate } = action.payload;
      state.db4ToDate = db4ToDate;
      state.needSearch = true;
    },
    selectDb4Appointment(state, action) {
      const { selectedDb4AppointmentId } = action.payload;
      state.selectedDb4AppointmentId = selectedDb4AppointmentId;
    },
    fetchDb4AppointmentsSuccess(state, action) {
      state.appointments = action.payload;
    },
    fetchDb4AppointmentDetailsSuccess(state, action) {
      const { id, details } = action.payload;
      state.cachedAppointmentDetails[id] = transformAppointment(details);
    },
    updateDb4ServiceItems(state, action) {
      const { field, value } = action.payload;
      state.cachedAppointmentDetails[state.selectedDb4AppointmentId].serviceItems[field] = value;
    },
    fillDb4ServiceItems(state, action) {
      const { id } = action.payload;
      if (_.has(serviceBenefitsMapping, id)) {
        state.cachedAppointmentDetails[state.selectedDb4AppointmentId].serviceItems = serviceBenefitsMapping[id];
      }
    },
    setDb4Notification(state, action) {
      const { appointmentId, dateTime } = action.payload;
      state.db4Notification[appointmentId] = dateTime;
    },
    setDb4AppointmentToDoctorNotification(state, action) {
      const { appointmentId, dateTime } = action.payload;
      state.db4AppointmentToDoctorNotification[appointmentId] = dateTime;
    },
    setDb4AppointmentToPatientNotification(state, action) {
      const { appointmentId, dateTime } = action.payload;
      state.db4AppointmentToPatientNotification[appointmentId] = dateTime;
    },
  },
});

export const {
  resetSearch,
  setDb4FromDate,
  setDb4ToDate,
  selectDb4Appointment,
  fetchDb4AppointmentsSuccess,
  fetchDb4AppointmentDetailsSuccess,
  updateDb4ServiceItems,
  fillDb4ServiceItems,
  setDb4Notification,
  setDb4AppointmentToDoctorNotification,
  setDb4AppointmentToPatientNotification,
} = db4Slice.actions;

export const fetchDb4Appointments = (fromDate, toDate, pages) => async (dispatch) => {
  try {
    const result = await fetchAppointmentsService({ fromDate, toDate, pages });
    const appointments = _.get(result, "data", []);
    dispatch(fetchDb4AppointmentsSuccess(appointments));
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchDb4AppointmentDetails = (appointmentId, practitionerId, clientId) => async (dispatch) => {
  try {
    const result = await fetchAppointmentDetailsService({
      appointmentId,
      practitionerId,
      clientId,
    });
    const details = _.get(result, "data", {});
    dispatch(
      fetchDb4AppointmentDetailsSuccess({
        id: appointmentId,
        details,
      })
    );
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchMultiPagesDb4AppointmentDetails = async (appointments) => {
  const calls = appointments
    .filter((apt) => ["Booked", "Attended", "Rescheduled"].includes(apt.status))
    .map((apt) => {
      const { appointmentId, practitionerId, clientId } = apt;
      return fetchAppointmentDetailsService({ appointmentId, practitionerId, clientId });
    });
  const results = await Promise.all(calls);
  const appointmentDetails = results.map((res) => {
    const details = _.get(res, "data", {});
    return transformAppointment(details);
  });
  return appointmentDetails;
};

export const sendDb4Email = (details) => async (dispatch) => {
  try {
    const {
      id,
      patientEmail,
      patientFirstName,
      patientSurname,
      practitionerFirstName,
      practitionerLastName,
      dateOfService,
      serviceItems,
    } = details;

    await sendDB4Service({
      appointmentId: id,
      patientEmail,
      patientFullName: `${patientFirstName} ${patientSurname}`,
      practitionerFullName: `${practitionerFirstName} ${practitionerLastName}`,
      dateOfService,
      serviceItems,
    });
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const sendAppointmentNotification = (appointment) => async (dispatch) => {
  try {
    await sendAppointmentNotificationService({ appointment });
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const sendPatientNotification = (appointment) => async (dispatch) => {
  try {
    await sendPatientNotificationService({ appointment });
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export default db4Slice.reducer;
